import React, { useState, useEffect } from 'react';
import { 
    bluetoothDeviceManager, 
    BluetoothDeviceInfo, 
    BluetoothReading, 
    MedicalDeviceType, 
    ConnectionStatus 
} from '../utils/bluetoothDeviceManager';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faSignal, 
    faSearch, 
    faLink, 
    faUnlink, 
    faDownload, 
    faSpinner, 
    faCheckCircle, 
    faExclamationTriangle,
    faHeartbeat,
    faThermometerHalf,
    faTint,
    faWeight,
    faRunning,
    faQuestion
} from '@fortawesome/free-solid-svg-icons';
import Swal from 'sweetalert2';

interface BluetoothDeviceManagerProps {
    onDeviceConnected?: (device: BluetoothDeviceInfo) => void;
    onDataReceived?: (reading: BluetoothReading) => void;
    onError?: (error: Error) => void;
}

const BluetoothDeviceManager: React.FC<BluetoothDeviceManagerProps> = ({
    onDeviceConnected,
    onDataReceived,
    onError
}) => {
    const [devices, setDevices] = useState<BluetoothDeviceInfo[]>([]);
    const [status, setStatus] = useState<ConnectionStatus>(ConnectionStatus.DISCONNECTED);
    const [isDiscovering, setIsDiscovering] = useState(false);
    const [selectedDevice, setSelectedDevice] = useState<BluetoothDeviceInfo | null>(null);
    const [lastReading, setLastReading] = useState<BluetoothReading | null>(null);

    useEffect(() => {
        // Set up event listeners
        bluetoothDeviceManager.on('devicesUpdated', setDevices);
        bluetoothDeviceManager.on('statusChanged', setStatus);
        bluetoothDeviceManager.on('deviceConnected', handleDeviceConnected);
        bluetoothDeviceManager.on('deviceDisconnected', handleDeviceDisconnected);
        bluetoothDeviceManager.on('dataReceived', handleDataReceived);
        bluetoothDeviceManager.on('error', handleError);

        // Initialize with current state
        setDevices(bluetoothDeviceManager.getDevices());
        setStatus(bluetoothDeviceManager.getStatus());
        setIsDiscovering(bluetoothDeviceManager.isDiscoveryInProgress());

        return () => {
            // Cleanup event listeners
            bluetoothDeviceManager.off('devicesUpdated', setDevices);
            bluetoothDeviceManager.off('statusChanged', setStatus);
            bluetoothDeviceManager.off('deviceConnected', handleDeviceConnected);
            bluetoothDeviceManager.off('deviceDisconnected', handleDeviceDisconnected);
            bluetoothDeviceManager.off('dataReceived', handleDataReceived);
            bluetoothDeviceManager.off('error', handleError);
        };
    }, []);

    const handleDeviceConnected = (device: BluetoothDeviceInfo) => {
        setSelectedDevice(device);
        onDeviceConnected?.(device);
        Swal.fire({
            icon: 'success',
            title: 'Device Connected!',
            text: `Successfully connected to ${device.name}`,
            timer: 2000,
            showConfirmButton: false
        });
    };

    const handleDeviceDisconnected = (device: BluetoothDeviceInfo) => {
        if (selectedDevice?.id === device.id) {
            setSelectedDevice(null);
        }
        Swal.fire({
            icon: 'info',
            title: 'Device Disconnected',
            text: `${device.name} has been disconnected`,
            timer: 2000,
            showConfirmButton: false
        });
    };

    const handleDataReceived = (reading: BluetoothReading) => {
        setLastReading(reading);
        onDataReceived?.(reading);
    };

    const handleError = (error: Error) => {
        console.error('Bluetooth error:', error);
        onError?.(error);
        Swal.fire({
            icon: 'error',
            title: 'Bluetooth Error',
            text: error.message,
        });
    };

    const startDiscovery = async () => {
        try {
            setIsDiscovering(true);
            await bluetoothDeviceManager.startDiscovery();
        } catch (error) {
            console.error('Discovery failed:', error);
        } finally {
            setIsDiscovering(false);
        }
    };

    const connectToDevice = async (device: BluetoothDeviceInfo) => {
        try {
            await bluetoothDeviceManager.connectToDevice(device.id);
        } catch (error) {
            console.error('Connection failed:', error);
        }
    };

    const disconnectDevice = async (device: BluetoothDeviceInfo) => {
        try {
            await bluetoothDeviceManager.disconnectDevice(device.id);
        } catch (error) {
            console.error('Disconnection failed:', error);
        }
    };

    const readDeviceData = async (device: BluetoothDeviceInfo) => {
        try {
            const reading = await bluetoothDeviceManager.readDeviceData(device.id);
            setLastReading(reading);
        } catch (error) {
            console.error('Data reading failed:', error);
        }
    };

    const getDeviceIcon = (deviceType: MedicalDeviceType) => {
        switch (deviceType) {
            case MedicalDeviceType.HEART_RATE_MONITOR:
                return faHeartbeat;
            case MedicalDeviceType.THERMOMETER:
                return faThermometerHalf;
            case MedicalDeviceType.BLOOD_PRESSURE:
                return faTint;
            case MedicalDeviceType.WEIGHT_SCALE:
                return faWeight;
            case MedicalDeviceType.FITNESS_TRACKER:
                return faRunning;
            default:
                return faQuestion;
        }
    };

    const getStatusColor = (status: ConnectionStatus) => {
        switch (status) {
            case ConnectionStatus.CONNECTED:
                return 'text-success';
            case ConnectionStatus.CONNECTING:
            case ConnectionStatus.DISCOVERING:
                return 'text-warning';
            case ConnectionStatus.ERROR:
                return 'text-danger';
            default:
                return 'text-secondary';
        }
    };

    const getStatusIcon = (status: ConnectionStatus) => {
        switch (status) {
            case ConnectionStatus.CONNECTED:
                return faCheckCircle;
            case ConnectionStatus.CONNECTING:
            case ConnectionStatus.DISCOVERING:
                return faSpinner;
            case ConnectionStatus.ERROR:
                return faExclamationTriangle;
            default:
                return faSignal;
        }
    };

        return (
        <div className="bluetooth-device-manager p-4">
            {/* Status and Scan Button */}
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div className={`d-flex align-items-center ${getStatusColor(status)}`}>
                    <FontAwesomeIcon
                        icon={getStatusIcon(status)}
                        className={`me-2 ${status === ConnectionStatus.CONNECTING || status === ConnectionStatus.DISCOVERING ? 'fa-spin' : ''}`}
                    />
                    <span className="text-capitalize fw-semibold">Status: {status.replace('_', ' ')}</span>
                </div>
                <button
                    className="btn btn-primary"
                    onClick={startDiscovery}
                    disabled={isDiscovering || status === ConnectionStatus.CONNECTING}
                >
                    <FontAwesomeIcon icon={isDiscovering ? faSpinner : faSearch} className={`me-2 ${isDiscovering ? 'fa-spin' : ''}`} />
                    {isDiscovering ? 'Scanning...' : 'Scan for Devices'}
                </button>
            </div>

            {/* Device List */}
            <div className="row">
                <div className="col-md-6">
                    <h5 className="mb-3">Available Devices</h5>
                    {devices.length === 0 ? (
                        <div className="text-center text-muted py-4">
                            <FontAwesomeIcon icon={faSignal} size="2x" className="mb-3" />
                            <p>No devices found. Click "Scan for Devices" to discover Bluetooth medical devices.</p>
                        </div>
                    ) : (
                        <div className="device-list">
                            {devices.map((device) => (
                                <div key={device.id} className="card mb-3 device-card">
                                    <div className="card-body">
                                        <div className="d-flex justify-content-between align-items-start">
                                            <div className="d-flex align-items-center">
                                                <FontAwesomeIcon 
                                                    icon={getDeviceIcon(device.deviceType)} 
                                                    className="me-3 text-primary"
                                                    size="lg"
                                                />
                                                <div>
                                                    <h6 className="mb-1">{device.name}</h6>
                                                    <small className="text-muted text-capitalize">
                                                        {device.deviceType.replace('_', ' ')}
                                                    </small>
                                                </div>
                                            </div>
                                            <div className="d-flex gap-2">
                                                {device.isConnected ? (
                                                    <>
                                                        <button
                                                            className="btn btn-sm btn-outline-primary"
                                                            onClick={() => readDeviceData(device)}
                                                            title="Read Data"
                                                        >
                                                            <FontAwesomeIcon icon={faDownload} />
                                                        </button>
                                                        <button
                                                            className="btn btn-sm btn-outline-danger"
                                                            onClick={() => disconnectDevice(device)}
                                                            title="Disconnect"
                                                        >
                                                            <FontAwesomeIcon icon={faUnlink} />
                                                        </button>
                                                    </>
                                                ) : (
                                                    <button
                                                        className="btn btn-sm btn-success"
                                                        onClick={() => connectToDevice(device)}
                                                        disabled={status === ConnectionStatus.CONNECTING}
                                                        title="Connect"
                                                    >
                                                        <FontAwesomeIcon icon={faLink} />
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                        {device.isConnected && (
                                            <div className="mt-2">
                                                <span className="badge bg-success">Connected</span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Connected Device Details */}
                <div className="col-md-6">
                    <h5 className="mb-3">Device Details</h5>
                    {selectedDevice ? (
                        <div className="card">
                            <div className="card-body">
                                <div className="d-flex align-items-center mb-3">
                                    <FontAwesomeIcon 
                                        icon={getDeviceIcon(selectedDevice.deviceType)} 
                                        className="me-3 text-primary"
                                        size="lg"
                                    />
                                    <div>
                                        <h6 className="mb-1">{selectedDevice.name}</h6>
                                        <small className="text-muted text-capitalize">
                                            {selectedDevice.deviceType.replace('_', ' ')}
                                        </small>
                                    </div>
                                </div>

                                <div className="mb-3">
                                    <strong>Device ID:</strong> {selectedDevice.id}
                                </div>

                                {selectedDevice.services && selectedDevice.services.length > 0 && (
                                    <div className="mb-3">
                                        <strong>Available Services:</strong>
                                        <ul className="list-unstyled mt-1">
                                            {selectedDevice.services.map((service, index) => (
                                                <li key={index} className="text-muted small">
                                                    {service}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}

                                <button
                                    className="btn btn-primary w-100"
                                    onClick={() => readDeviceData(selectedDevice)}
                                >
                                    <FontAwesomeIcon icon={faDownload} className="me-2" />
                                    Read Latest Data
                                </button>
                            </div>
                        </div>
                    ) : (
                        <div className="text-center text-muted py-4">
                            <FontAwesomeIcon icon={faSignal} size="2x" className="mb-3" />
                            <p>No device selected. Connect to a device to view details.</p>
                        </div>
                    )}

                    {/* Latest Reading */}
                    {lastReading && (
                        <div className="card mt-3">
                            <div className="card-header">
                                <h6 className="mb-0">Latest Reading</h6>
                            </div>
                            <div className="card-body">
                                <div className="mb-2">
                                    <small className="text-muted">
                                        {new Date(lastReading.timestamp).toLocaleString()}
                                    </small>
                                </div>
                                {lastReading.readings.map((reading, index) => (
                                    <div key={index} className="d-flex justify-content-between mb-1">
                                        <span>{reading.name}:</span>
                                        <strong>{reading.value} {reading.unit}</strong>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Connection Instructions */}
            <div className="alert alert-info mt-4">
                <h6 className="mb-2">How to Connect:</h6>
                <ol className="mb-2 small">
                    <li>Click "Scan for Devices" to discover nearby Bluetooth medical devices</li>
                    <li>Select your device from the list and click the connect button</li>
                    <li>Follow the browser's Bluetooth pairing dialog</li>
                    <li>Once connected, you can read data from the device</li>
                </ol>
                <small className="text-muted">
                    <strong>Note:</strong> Web Bluetooth API requires HTTPS and is supported in Chrome, Edge, and other Chromium-based browsers.
                </small>
            </div>
        </div>
    );
};

export default BluetoothDeviceManager;
